<template>
    <div class="cf-container">
        <div class="cf-action-container">
            <md-button
                title="Add team custom fields"
                class="md-primary md-just-icon md-round"
                @click.stop="handleAddCustomField()"
            >
                <md-icon>add</md-icon>
            </md-button>
        </div>
        <md-table class="custom-paginated-table">
            <md-table-row>
                <md-table-head>Group</md-table-head>
                <md-table-head>Label</md-table-head>
                <md-table-head>Type</md-table-head>
                <md-table-head class="actions">Actions</md-table-head>
            </md-table-row>

            <md-table-row v-for="(customField, index) in tandemGroups" :key="`${customField.group}-${index}-tandem`">
                <md-table-cell>
                    {{ customField.label }}
                </md-table-cell>
                <md-table-cell>
                    {{ `${customField.fields[0].label} + ${customField.fields[1].label}` }}
                </md-table-cell>
                <md-table-cell>
                    {{
                        `${$_camelCaseToSentenceCase(customField.fields[0].type)} + ${$_camelCaseToSentenceCase(
                            customField.fields[1].type
                        )}`
                    }}
                </md-table-cell>
                <md-table-cell class="action-buttons">
                    <md-button
                        title="edit custom field"
                        class="md-warning md-just-icon md-round"
                        @click.stop="handleEditCustomField(customField)"
                    >
                        <md-icon>edit</md-icon>
                    </md-button>
                    <md-button
                        title="delete custom field"
                        class="md-danger md-just-icon md-round"
                        @click.stop="handleRemoveCustomField(customField.group)"
                    >
                        <md-icon>delete</md-icon>
                    </md-button>
                </md-table-cell>
            </md-table-row>
            <md-table-row v-for="(fld, index) in standaloneFieldsGroup" :key="`${fld.name}-${index}-standaloneFields`">
                <md-table-cell>
                    &nbsp;
                </md-table-cell>
                <md-table-cell>
                    {{ fld.label }}
                </md-table-cell>
                <md-table-cell>
                    {{ $_camelCaseToSentenceCase(fld.type) }}
                </md-table-cell>
                <md-table-cell class="action-buttons">
                    <md-button
                        title="edit custom field"
                        class="md-warning md-just-icon md-round"
                        @click.stop="handleEditCustomField(fld, standaloneFieldsGroupName)"
                    >
                        <md-icon>edit</md-icon>
                    </md-button>
                    <md-button
                        title="delete custom field"
                        class="md-danger md-just-icon md-round"
                        @click.stop="handleRemoveCustomField(standaloneFieldsGroupName, fld.name)"
                    >
                        <md-icon>delete</md-icon>
                    </md-button>
                </md-table-cell>
            </md-table-row>
        </md-table>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { fieldGroupOptions, STANDALONE_FIELDS_GROUP } from '@/utils/customFields';
import TeamMemberCustomFieldModal from './TeamMemberCustomFieldModal';

export default {
    name: 'TeamMemberCustomFieldsList',
    mixins: [GeneralMixin],
    data() {
        return {
            customFieldList: [],
            standaloneFieldsGroupName: STANDALONE_FIELDS_GROUP
        };
    },
    computed: {
        standaloneFieldsGroup() {
            return this.customFieldList.find((option) => option.group === STANDALONE_FIELDS_GROUP)?.fields || [];
        },
        tandemGroups() {
            return this.customFieldList.filter((option) => option.group !== STANDALONE_FIELDS_GROUP);
        }
    },
    methods: {
        async getCustomFields() {
            try {
                this.$_handleLoaderState(true);
                const api = '/api/teams/custom-fields/team-members';
                const { data } = await handleRequests(api);
                if (data && Array.isArray(data)) {
                    this.customFieldList = data;
                    return;
                }
                this.customFieldList = [];
            } catch (e) {
                const message = 'Failed to fetch custom fields.';
                showErrorMessage(this, message, e);
                this.customFieldList = [];
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        handleAddCustomField() {
            this.$modal
                .show(TeamMemberCustomFieldModal, {
                    isEdit: false,
                    checkDuplicateGroup: this.checkDuplicateGroup,
                    getFieldGroupOptions: this.getFieldGroupOptions,
                    hasDuplicateNameInStandaloneFields: this.hasDuplicateNameInStandaloneFields
                })
                .then((result) => {
                    if (result) {
                        if (result.group === STANDALONE_FIELDS_GROUP) {
                            // if it's a standaloneFields field, add it to the standaloneFields group
                            const standaloneFieldsGroup = this.customFieldList.find(
                                (field) => field.group === result.group
                            );
                            if (standaloneFieldsGroup) {
                                standaloneFieldsGroup.fields.push(result.fields[0]);
                                return;
                            }
                        }

                        // otherwise, add it as a new group
                        this.customFieldList = [...this.customFieldList, result];
                    }
                });
            this.$emit('add-custom-field');
        },
        handleEditCustomField(customField, group) {
            let updCustomField = {};

            if (group === STANDALONE_FIELDS_GROUP) {
                // format in 'group'
                updCustomField = {
                    group,
                    label: 'Custom Fields', // label does not matter
                    fields: [{ ...customField }]
                };
            } else {
                updCustomField = { ...customField };
            }

            this.$modal
                .show(TeamMemberCustomFieldModal, {
                    isEdit: true,
                    customField: updCustomField,
                    checkDuplicateGroup: this.checkDuplicateGroup
                })
                .then((result) => {
                    if (!result) 
                        return;

                    const groupIndex = this.customFieldList.findIndex((f) => f.group === result.group);

                    if (groupIndex === -1) 
                        return;

                    if (result.group === STANDALONE_FIELDS_GROUP) {
                        const standaloneFieldsGroup = this.customFieldList.find(
                            (f) => f.group === STANDALONE_FIELDS_GROUP
                        );

                        if (standaloneFieldsGroup) {
                            const fieldIndex = standaloneFieldsGroup.fields.findIndex(
                                (f) => f.name === customField.name
                            );

                            if (fieldIndex !== -1) {
                                // Replace specific field in standaloneFields group (preserving reactivity)
                                this.$set(standaloneFieldsGroup.fields, fieldIndex, result.fields[0]);
                            } else {
                                // Add new field reactively
                                standaloneFieldsGroup.fields.push(result.fields[0]);
                            }

                            // Manually trigger reactivity by resetting the entire group object
                            this.$set(this.customFieldList, groupIndex, { ...standaloneFieldsGroup });
                        } else {
                            this.customFieldList.push(result);
                        }
                    } else {
                        // Replace entire group (preserving reactivity)
                        this.$set(this.customFieldList, groupIndex, result);
                    }
                });
        },
        handleRemoveCustomField(group, fieldName) {
            this.$messageBox
                .show({
                    title: 'Delete team member custom field',
                    body: 'Are you sure you want to delete this custom field?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.removeCustomField(group, fieldName);
                    }
                });
        },
        async removeCustomField(group, fieldName) {
            this.$_handleLoaderState(true, 'Deleting custom fields...');
            const payload = {
                method: 'delete',
                data: { group, fieldName }
            };
            const api = `/api/teams/custom-fields/team-members`;

            try {
                await handleRequests(api, payload);

                this.$notify({
                    message: `Custom fields removed!`,
                    type: 'success'
                });

                await this.$store.dispatch('user/FETCH_USER');

                const groupIndex = this.customFieldList.findIndex((f) => f.group === group);

                if (group !== STANDALONE_FIELDS_GROUP) {
                    this.customFieldList = this.customFieldList.filter((field) => field.group !== group);
                } else {
                    const standaloneFieldsGroup = this.customFieldList.find((f) => f.group === STANDALONE_FIELDS_GROUP);
                    standaloneFieldsGroup.fields = standaloneFieldsGroup.fields.filter(
                        (field) => field.name !== fieldName
                    );
                    this.$set(this.customFieldList, groupIndex, { ...standaloneFieldsGroup });
                }
            } catch (e) {
                const message = 'Cannot remove custom fields.';
                showErrorMessage(this, message, e);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        checkDuplicateGroup(newGroupName) {
            const isDuplicate = this.customFieldList.some((field) => field.group === newGroupName);
            if (isDuplicate) {
                showErrorMessage(this, 'Custom field group name already exists.');
                return true;
            }
            return false;
        },
        getFieldGroupOptions() {
            const filtered = fieldGroupOptions.filter((option) => {
                return !this.customFieldList.some((field) => field.group === option.value);
            });

            return filtered.sort((a, b) => a.label.localeCompare(b.label));
        },
        hasDuplicateNameInStandaloneFields(newFieldName) {
            return this.standaloneFieldsGroup.some((field) => field.name === newFieldName);
        }
    },
    async mounted() {
        await this.getCustomFields();
    }
};
</script>
